import { Email, Home, LocalPostOffice, LocationOn, Phone, Print } from '@mui/icons-material';
import {
    Box,
    Card,
    CardContent,
    Chip,
    Container,
    Grid,
    Typography
} from '@mui/material';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { useTheme } from '../contexts/ThemeContext';

const Contact = () => {
    const { mode } = useTheme();
    const { t } = useTranslation();

    const scrollToSection = (sectionId) => {
        const element = document.getElementById(sectionId);
        if (element) {
            const navbarHeight = 120; // Height of sticky header
            const elementPosition = element.getBoundingClientRect().top;
            const offsetPosition = elementPosition + window.pageYOffset - navbarHeight;

            window.scrollTo({
                top: offsetPosition,
                behavior: 'smooth'
            });
        }
    };

    return (
        <Box
            sx={{
                minHeight: '100vh',
                position: 'relative',
                overflow: 'hidden',
                '&::before': {
                    content: '""',
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    background: (theme) =>
                        theme.palette.mode === 'dark'
                            ? 'radial-gradient(ellipse at top, #1e3a5f 0%, #0f2642 100%)'
                            : 'radial-gradient(ellipse at top, #f8fafc 0%, #e2e8f0 100%)',
                    zIndex: 0,
                },
                '&::after': {
                    content: '""',
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    background:
                        'radial-gradient(circle at 20% 50%, rgba(33, 150, 243, 0.1) 0%, transparent 50%), ' +
                        'radial-gradient(circle at 80% 80%, rgba(139, 92, 246, 0.1) 0%, transparent 50%)',
                    pointerEvents: 'none',
                    zIndex: 0,
                },
            }}
        >
            <Container maxWidth="xl" sx={{ position: 'relative', zIndex: 1 }}>
                {/* Hero Section */}
                <Box
                    sx={{
                        py: { xs: 6, sm: 8, md: 12 },
                        textAlign: 'center',
                        position: 'relative',
                        zIndex: 1,
                        px: { xs: 2, sm: 3, md: 4 },
                    }}
                >
                    <Typography
                        variant="h1"
                        sx={{
                            fontSize: { xs: '2rem', sm: '2.75rem', md: '3.5rem', lg: '4rem' },
                            fontWeight: 900,
                            mb: { xs: 2, sm: 3 },
                            background: (theme) =>
                                theme.palette.mode === 'dark'
                                    ? 'linear-gradient(135deg, #fff 0%, #94a3b8 100%)'
                                    : 'linear-gradient(135deg, #0f172a 0%, #334155 100%)',
                            WebkitBackgroundClip: 'text',
                            WebkitTextFillColor: 'transparent',
                            backgroundClip: 'text',
                            lineHeight: 1.2,
                            letterSpacing: '-0.02em',
                        }}
                    >
                        {t('contact.title')}
                    </Typography>
                    <Typography
                        variant="h2"
                        sx={{
                            fontSize: { xs: '1.25rem', sm: '1.5rem', md: '2rem' },
                            fontWeight: 600,
                            mb: 4,
                            background: 'linear-gradient(90deg, #2196f3 0%, #21cbf3 100%)',
                            WebkitBackgroundClip: 'text',
                            WebkitTextFillColor: 'transparent',
                            backgroundClip: 'text',
                        }}
                    >
                        {t('contact.subtitle')}
                    </Typography>

                    {/* Contact Features */}
                    <Box
                        sx={{
                            display: 'flex',
                            justifyContent: 'center',
                            flexWrap: 'wrap',
                            gap: 2,
                            mb: 6,
                        }}
                    >
                        <Chip
                            icon={<Phone />}
                            label={t('contact.features.phone')}
                            onClick={() => scrollToSection('phone-section')}
                            sx={{
                                px: 2,
                                py: 3,
                                fontSize: { xs: '0.875rem', md: '1rem' },
                                fontWeight: 500,
                                backgroundColor: (theme) =>
                                    theme.palette.mode === 'dark'
                                        ? 'rgba(33, 150, 243, 0.15)'
                                        : 'rgba(33, 150, 243, 0.1)',
                                backdropFilter: 'blur(10px)',
                                border: '1px solid rgba(33, 150, 243, 0.3)',
                                color: (theme) =>
                                    theme.palette.mode === 'dark' ? '#fff' : '#1976d2',
                                cursor: 'pointer',
                                '& .MuiChip-icon': {
                                    color: '#2196f3',
                                    fontSize: '1.5rem',
                                },
                                transition: 'all 0.3s ease',
                                '&:hover': {
                                    transform: 'translateY(-4px)',
                                    backgroundColor: 'rgba(33, 150, 243, 0.25)',
                                    boxShadow: '0 8px 24px rgba(33, 150, 243, 0.3)',
                                },
                            }}
                        />
                        <Chip
                            icon={<Email />}
                            label={t('contact.features.email')}
                            onClick={() => scrollToSection('email-section')}
                            sx={{
                                px: 2,
                                py: 3,
                                fontSize: { xs: '0.875rem', md: '1rem' },
                                fontWeight: 500,
                                backgroundColor: (theme) =>
                                    theme.palette.mode === 'dark'
                                        ? 'rgba(33, 150, 243, 0.15)'
                                        : 'rgba(33, 150, 243, 0.1)',
                                backdropFilter: 'blur(10px)',
                                border: '1px solid rgba(33, 150, 243, 0.3)',
                                color: (theme) =>
                                    theme.palette.mode === 'dark' ? '#fff' : '#1976d2',
                                cursor: 'pointer',
                                '& .MuiChip-icon': {
                                    color: '#2196f3',
                                    fontSize: '1.5rem',
                                },
                                transition: 'all 0.3s ease',
                                '&:hover': {
                                    transform: 'translateY(-4px)',
                                    backgroundColor: 'rgba(33, 150, 243, 0.25)',
                                    boxShadow: '0 8px 24px rgba(33, 150, 243, 0.3)',
                                },
                            }}
                        />
                        <Chip
                            icon={<LocationOn />}
                            label={t('contact.features.location')}
                            onClick={() => scrollToSection('location-section')}
                            sx={{
                                px: 2,
                                py: 3,
                                fontSize: { xs: '0.875rem', md: '1rem' },
                                fontWeight: 500,
                                backgroundColor: (theme) =>
                                    theme.palette.mode === 'dark'
                                        ? 'rgba(33, 150, 243, 0.15)'
                                        : 'rgba(33, 150, 243, 0.1)',
                                backdropFilter: 'blur(10px)',
                                border: '1px solid rgba(33, 150, 243, 0.3)',
                                color: (theme) =>
                                    theme.palette.mode === 'dark' ? '#fff' : '#1976d2',
                                cursor: 'pointer',
                                '& .MuiChip-icon': {
                                    color: '#2196f3',
                                    fontSize: '1.5rem',
                                },
                                transition: 'all 0.3s ease',
                                '&:hover': {
                                    transform: 'translateY(-4px)',
                                    backgroundColor: 'rgba(33, 150, 243, 0.25)',
                                    boxShadow: '0 8px 24px rgba(33, 150, 243, 0.3)',
                                },
                            }}
                        />
                    </Box>
                </Box>

                {/* Main Content Section */}
                <Box sx={{ mb: 8 }}>
                    <Card
                        sx={{
                            backgroundColor: (theme) =>
                                theme.palette.mode === 'dark'
                                    ? 'rgba(255, 255, 255, 0.05)'
                                    : 'rgba(255, 255, 255, 0.8)',
                            backdropFilter: 'blur(20px)',
                            border: '1px solid rgba(255, 255, 255, 0.2)',
                            borderRadius: 3,
                            p: { xs: 3, md: 5 },
                            boxShadow: '0 8px 32px rgba(0, 0, 0, 0.1)',
                            transition: 'all 0.3s ease',
                            '&:hover': {
                                transform: 'translateY(-4px)',
                                boxShadow: '0 16px 48px rgba(0, 0, 0, 0.15)',
                            },
                        }}
                    >
                        <Typography
                            variant="h4"
                            sx={{
                                fontSize: { xs: '1.5rem', md: '2rem' },
                                fontWeight: 600,
                                mb: 3,
                                color: (theme) =>
                                    theme.palette.mode === 'dark' ? '#ffffff' : '#1a1a1a',
                                textAlign: 'center',
                            }}
                        >
                            {t('contact.mainQuestion')}
                        </Typography>

                        <Typography
                            variant="body1"
                            sx={{
                                fontSize: { xs: '1rem', md: '1.1rem' },
                                lineHeight: 1.8,
                                mb: 3,
                                color: (theme) =>
                                    theme.palette.mode === 'dark'
                                        ? 'rgba(255, 255, 255, 0.85)'
                                        : 'rgba(0, 0, 0, 0.75)',
                                textAlign: 'center',
                            }}
                        >
                            {t('contact.description1')}
                        </Typography>

                        <Typography
                            variant="body1"
                            sx={{
                                fontSize: { xs: '1rem', md: '1.1rem' },
                                lineHeight: 1.8,
                                mb: 3,
                                color: (theme) =>
                                    theme.palette.mode === 'dark'
                                        ? 'rgba(255, 255, 255, 0.85)'
                                        : 'rgba(0, 0, 0, 0.75)',
                                textAlign: 'center',
                            }}
                        >
                            {t('contact.description2')}
                        </Typography>

                        <Typography
                            variant="body1"
                            sx={{
                                fontSize: { xs: '1rem', md: '1.1rem' },
                                lineHeight: 1.8,
                                mb: 4,
                                color: (theme) =>
                                    theme.palette.mode === 'dark'
                                        ? 'rgba(255, 255, 255, 0.85)'
                                        : 'rgba(0, 0, 0, 0.75)',
                                textAlign: 'center',
                            }}
                        >
                            {t('contact.description3')}
                        </Typography>
                    </Card>
                </Box>

                <Grid container spacing={4}>

                    {/* Company Contact Information */}
                    <Grid item xs={12}>
                        <Box sx={{ mb: 6 }}>
                            <Typography
                                variant="h3"
                                sx={{
                                    fontSize: { xs: '1.8rem', md: '2.5rem' },
                                    fontWeight: 'bold',
                                    mb: 4,
                                    textAlign: 'center',
                                    background: (theme) =>
                                        theme.palette.mode === 'dark'
                                            ? 'linear-gradient(135deg, #fff 0%, #94a3b8 100%)'
                                            : 'linear-gradient(135deg, #0f172a 0%, #334155 100%)',
                                    WebkitBackgroundClip: 'text',
                                    WebkitTextFillColor: 'transparent',
                                    backgroundClip: 'text',
                                }}
                            >
                                {t('contact.companyInfo')}
                            </Typography>

                            <Grid container spacing={4}>
                                {/* Company Telephone Numbers */}
                                <Grid item xs={12} md={6} id="phone-section">
                                    <Card
                                        sx={{
                                            backgroundColor: (theme) =>
                                                theme.palette.mode === 'dark'
                                                    ? 'rgba(255, 255, 255, 0.05)'
                                                    : 'rgba(255, 255, 255, 0.8)',
                                            backdropFilter: 'blur(20px)',
                                            border: '1px solid rgba(255, 255, 255, 0.2)',
                                            borderRadius: 3,
                                            height: '100%',
                                            boxShadow: '0 8px 32px rgba(0, 0, 0, 0.1)',
                                            transition: 'all 0.3s ease',
                                            '&:hover': {
                                                transform: 'translateY(-4px)',
                                                boxShadow: '0 16px 48px rgba(0, 0, 0, 0.15)',
                                            },
                                        }}
                                    >
                                        <CardContent sx={{ p: 3 }}>
                                            <Typography
                                                variant="h5"
                                                sx={{
                                                    fontWeight: 600,
                                                    mb: 3,
                                                    color: (theme) => theme.palette.mode === 'dark' ? '#ffffff' : '#000000',
                                                    textAlign: 'center',
                                                }}
                                            >
                                                {t('contact.companyPhones')}
                                            </Typography>

                                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                                    <Phone sx={{ color: (theme) => theme.palette.mode === 'dark' ? '#ffffff' : '#000000', fontSize: '1.5rem' }} />
                                                    <Box>
                                                        <Typography variant="body1" sx={{ fontWeight: 500, color: (theme) => theme.palette.mode === 'dark' ? '#ffffff' : '#000000' }}>
                                                            {t('contact.phoneNumbers.demis')}
                                                        </Typography>
                                                    </Box>
                                                </Box>

                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                                    <Phone sx={{ color: (theme) => theme.palette.mode === 'dark' ? '#ffffff' : '#000000', fontSize: '1.5rem' }} />
                                                    <Box>
                                                        <Typography variant="body1" sx={{ fontWeight: 500, color: (theme) => theme.palette.mode === 'dark' ? '#ffffff' : '#000000' }}>
                                                            {t('contact.phoneNumbers.smartDecision')}
                                                        </Typography>
                                                    </Box>
                                                </Box>

                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                                    <Phone sx={{ color: (theme) => theme.palette.mode === 'dark' ? '#ffffff' : '#000000', fontSize: '1.5rem' }} />
                                                    <Box>
                                                        <Typography variant="body1" sx={{ fontWeight: 500, color: (theme) => theme.palette.mode === 'dark' ? '#ffffff' : '#000000' }}>
                                                            {t('contact.phoneNumbers.ariaVoice')}
                                                        </Typography>
                                                    </Box>
                                                </Box>

                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                                    <Phone sx={{ color: (theme) => theme.palette.mode === 'dark' ? '#ffffff' : '#000000', fontSize: '1.5rem' }} />
                                                    <Box>
                                                        <Typography variant="body1" sx={{ fontWeight: 500, color: (theme) => theme.palette.mode === 'dark' ? '#ffffff' : '#000000' }}>
                                                            {t('contact.phoneNumbers.fahimArgham')}
                                                        </Typography>
                                                    </Box>
                                                </Box>

                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                                    <Phone sx={{ color: (theme) => theme.palette.mode === 'dark' ? '#ffffff' : '#000000', fontSize: '1.5rem' }} />
                                                    <Box>
                                                        <Typography variant="body1" sx={{ fontWeight: 500, color: (theme) => theme.palette.mode === 'dark' ? '#ffffff' : '#000000' }}>
                                                            {t('contact.phoneNumbers.sephahanHealth')}
                                                        </Typography>
                                                    </Box>
                                                </Box>

                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                                    <Print sx={{ color: (theme) => theme.palette.mode === 'dark' ? '#ffffff' : '#000000', fontSize: '1.5rem' }} />
                                                    <Box>
                                                        <Typography variant="body1" sx={{ fontWeight: 500, color: (theme) => theme.palette.mode === 'dark' ? '#ffffff' : '#000000' }}>
                                                            {t('contact.phoneNumbers.fax')}
                                                        </Typography>
                                                    </Box>
                                                </Box>
                                            </Box>
                                        </CardContent>
                                    </Card>
                                </Grid>

                                {/* Call Center Phones */}
                                <Grid item xs={12} md={6}>
                                    <Card
                                        sx={{
                                            backgroundColor: (theme) =>
                                                theme.palette.mode === 'dark'
                                                    ? 'rgba(255, 255, 255, 0.05)'
                                                    : 'rgba(255, 255, 255, 0.8)',
                                            backdropFilter: 'blur(20px)',
                                            border: '1px solid rgba(255, 255, 255, 0.2)',
                                            borderRadius: 3,
                                            height: '100%',
                                            boxShadow: '0 8px 32px rgba(0, 0, 0, 0.1)',
                                            transition: 'all 0.3s ease',
                                            '&:hover': {
                                                transform: 'translateY(-4px)',
                                                boxShadow: '0 16px 48px rgba(0, 0, 0, 0.15)',
                                            },
                                        }}
                                    >
                                        <CardContent sx={{ p: 3 }}>
                                            <Typography
                                                variant="h5"
                                                sx={{
                                                    fontWeight: 600,
                                                    mb: 3,
                                                    color: (theme) => theme.palette.mode === 'dark' ? '#ffffff' : '#000000',
                                                    textAlign: 'center',
                                                }}
                                            >
                                                {t('contact.callCenterPhones')}
                                            </Typography>

                                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                                    <Phone sx={{ color: (theme) => theme.palette.mode === 'dark' ? '#ffffff' : '#000000', fontSize: '1.5rem' }} />
                                                    <Box>
                                                        <Typography variant="body1" sx={{ fontWeight: 500, color: (theme) => theme.palette.mode === 'dark' ? '#ffffff' : '#000000' }}>
                                                            {t('contact.callCenterNumbers.electronicExchange')}
                                                        </Typography>
                                                    </Box>
                                                </Box>

                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                                    <Phone sx={{ color: (theme) => theme.palette.mode === 'dark' ? '#ffffff' : '#000000', fontSize: '1.5rem' }} />
                                                    <Box>
                                                        <Typography variant="body1" sx={{ fontWeight: 500, color: (theme) => theme.palette.mode === 'dark' ? '#ffffff' : '#000000' }}>
                                                            {t('contact.callCenterNumbers.rasaInsurance')}
                                                        </Typography>
                                                    </Box>
                                                </Box>

                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                                    <Phone sx={{ color: (theme) => theme.palette.mode === 'dark' ? '#ffffff' : '#000000', fontSize: '1.5rem' }} />
                                                    <Box>
                                                        <Typography variant="body1" sx={{ fontWeight: 500, color: (theme) => theme.palette.mode === 'dark' ? '#ffffff' : '#000000' }}>
                                                            {t('contact.callCenterNumbers.easyMed')}
                                                        </Typography>
                                                    </Box>
                                                </Box>

                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                                    <Phone sx={{ color: (theme) => theme.palette.mode === 'dark' ? '#ffffff' : '#000000', fontSize: '1.5rem' }} />
                                                    <Box>
                                                        <Typography variant="body1" sx={{ fontWeight: 500, color: (theme) => theme.palette.mode === 'dark' ? '#ffffff' : '#000000' }}>
                                                            {t('contact.callCenterNumbers.rasateb')}
                                                        </Typography>
                                                    </Box>
                                                </Box>
                                            </Box>
                                        </CardContent>
                                    </Card>
                                </Grid>

                                {/* Addresses */}
                                <Grid item xs={12} md={6} id="location-section">
                                    <Card
                                        sx={{
                                            backgroundColor: (theme) =>
                                                theme.palette.mode === 'dark'
                                                    ? 'rgba(255, 255, 255, 0.05)'
                                                    : 'rgba(255, 255, 255, 0.8)',
                                            backdropFilter: 'blur(20px)',
                                            border: '1px solid rgba(255, 255, 255, 0.2)',
                                            borderRadius: 3,
                                            height: '100%',
                                            boxShadow: '0 8px 32px rgba(0, 0, 0, 0.1)',
                                            transition: 'all 0.3s ease',
                                            '&:hover': {
                                                transform: 'translateY(-4px)',
                                                boxShadow: '0 16px 48px rgba(0, 0, 0, 0.15)',
                                            },
                                        }}
                                    >
                                        <CardContent sx={{ p: 3 }}>
                                            <Typography
                                                variant="h5"
                                                sx={{
                                                    fontWeight: 600,
                                                    mb: 3,
                                                    color: (theme) => theme.palette.mode === 'dark' ? '#ffffff' : '#000000',
                                                    textAlign: 'center',
                                                }}
                                            >
                                                {t('contact.address')}
                                            </Typography>

                                            <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
                                                <Home sx={{ color: (theme) => theme.palette.mode === 'dark' ? '#ffffff' : '#000000', fontSize: '1.5rem', mt: 0.5 }} />
                                                <Box>
                                                    <Typography variant="body1" sx={{ fontWeight: 500, color: (theme) => theme.palette.mode === 'dark' ? '#ffffff' : '#000000', lineHeight: 1.6 }}>
                                                        {t('contact.addresses.isfahan')}
                                                    </Typography>
                                                </Box>
                                            </Box>
                                        </CardContent>
                                    </Card>
                                </Grid>

                                {/* Email */}
                                <Grid item xs={12} md={6} id="email-section">
                                    <Card
                                        sx={{
                                            backgroundColor: (theme) =>
                                                theme.palette.mode === 'dark'
                                                    ? 'rgba(255, 255, 255, 0.05)'
                                                    : 'rgba(255, 255, 255, 0.8)',
                                            backdropFilter: 'blur(20px)',
                                            border: '1px solid rgba(255, 255, 255, 0.2)',
                                            borderRadius: 3,
                                            height: '100%',
                                            boxShadow: '0 8px 32px rgba(0, 0, 0, 0.1)',
                                            transition: 'all 0.3s ease',
                                            '&:hover': {
                                                transform: 'translateY(-4px)',
                                                boxShadow: '0 16px 48px rgba(0, 0, 0, 0.15)',
                                            },
                                        }}
                                    >
                                        <CardContent sx={{ p: 3 }}>
                                            <Typography
                                                variant="h5"
                                                sx={{
                                                    fontWeight: 600,
                                                    mb: 3,
                                                    color: (theme) => theme.palette.mode === 'dark' ? '#ffffff' : '#000000',
                                                    textAlign: 'center',
                                                }}
                                            >
                                                {t('footer.email')}
                                            </Typography>

                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                                <Email sx={{ color: (theme) => theme.palette.mode === 'dark' ? '#ffffff' : '#000000', fontSize: '1.5rem' }} />
                                                <Box>
                                                    <Typography variant="body1" sx={{ fontWeight: 500, color: (theme) => theme.palette.mode === 'dark' ? '#ffffff' : '#000000' }}>
                                                        <Box component="a" href="mailto:info@demisco.com" sx={{ color: '#3b82f6', textDecoration: 'none', '&:hover': { textDecoration: 'underline' } }}>
                                                            {t('footer.emailAddress')}
                                                        </Box>
                                                    </Typography>
                                                </Box>
                                            </Box>
                                        </CardContent>
                                    </Card>
                                </Grid>

                                {/* Tehran Office */}
                                <Grid item xs={12} md={6}>
                                    <Card
                                        sx={{
                                            backgroundColor: (theme) =>
                                                theme.palette.mode === 'dark'
                                                    ? 'rgba(255, 255, 255, 0.05)'
                                                    : 'rgba(255, 255, 255, 0.8)',
                                            backdropFilter: 'blur(20px)',
                                            border: '1px solid rgba(255, 255, 255, 0.2)',
                                            borderRadius: 3,
                                            height: '100%',
                                            boxShadow: '0 8px 32px rgba(0, 0, 0, 0.1)',
                                            transition: 'all 0.3s ease',
                                            '&:hover': {
                                                transform: 'translateY(-4px)',
                                                boxShadow: '0 16px 48px rgba(0, 0, 0, 0.15)',
                                            },
                                        }}
                                    >
                                        <CardContent sx={{ p: 3 }}>
                                            <Typography
                                                variant="h5"
                                                sx={{
                                                    fontWeight: 600,
                                                    mb: 3,
                                                    color: (theme) => theme.palette.mode === 'dark' ? '#ffffff' : '#000000',
                                                    textAlign: 'center',
                                                }}
                                            >
                                                {t('contact.tehranOffice')}
                                            </Typography>

                                            <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
                                                <Home sx={{ color: (theme) => theme.palette.mode === 'dark' ? '#ffffff' : '#000000', fontSize: '1.5rem', mt: 0.5 }} />
                                                <Box>
                                                    <Typography variant="body1" sx={{ fontWeight: 500, color: (theme) => theme.palette.mode === 'dark' ? '#ffffff' : '#000000', lineHeight: 1.6 }}>
                                                        {t('contact.addresses.tehran')}
                                                    </Typography>
                                                </Box>
                                            </Box>
                                        </CardContent>
                                    </Card>
                                </Grid>

                                {/* Postal Codes */}
                                <Grid item xs={12} md={6}>
                                    <Card
                                        sx={{
                                            backgroundColor: (theme) =>
                                                theme.palette.mode === 'dark'
                                                    ? 'rgba(255, 255, 255, 0.05)'
                                                    : 'rgba(255, 255, 255, 0.8)',
                                            backdropFilter: 'blur(20px)',
                                            border: '1px solid rgba(255, 255, 255, 0.2)',
                                            borderRadius: 3,
                                            height: '100%',
                                            boxShadow: '0 8px 32px rgba(0, 0, 0, 0.1)',
                                            transition: 'all 0.3s ease',
                                            '&:hover': {
                                                transform: 'translateY(-4px)',
                                                boxShadow: '0 16px 48px rgba(0, 0, 0, 0.15)',
                                            },
                                        }}
                                    >
                                        <CardContent sx={{ p: 3 }}>
                                            <Typography
                                                variant="h5"
                                                sx={{
                                                    fontWeight: 600,
                                                    mb: 3,
                                                    color: (theme) => theme.palette.mode === 'dark' ? '#ffffff' : '#000000',
                                                    textAlign: 'center',
                                                }}
                                            >
                                                {t('contact.postalCodes')}
                                            </Typography>

                                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                                    <LocalPostOffice sx={{ color: (theme) => theme.palette.mode === 'dark' ? '#ffffff' : '#000000', fontSize: '1.5rem' }} />
                                                    <Box>
                                                        <Typography variant="body1" sx={{ fontWeight: 500, color: (theme) => theme.palette.mode === 'dark' ? '#ffffff' : '#000000' }}>
                                                            {t('contact.postalCodesList.demis')}
                                                        </Typography>
                                                    </Box>
                                                </Box>

                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                                    <LocalPostOffice sx={{ color: (theme) => theme.palette.mode === 'dark' ? '#ffffff' : '#000000', fontSize: '1.5rem' }} />
                                                    <Box>
                                                        <Typography variant="body1" sx={{ fontWeight: 500, color: (theme) => theme.palette.mode === 'dark' ? '#ffffff' : '#000000' }}>
                                                            {t('contact.postalCodesList.fahimArgham')}
                                                        </Typography>
                                                    </Box>
                                                </Box>

                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                                    <LocalPostOffice sx={{ color: (theme) => theme.palette.mode === 'dark' ? '#ffffff' : '#000000', fontSize: '1.5rem' }} />
                                                    <Box>
                                                        <Typography variant="body1" sx={{ fontWeight: 500, color: (theme) => theme.palette.mode === 'dark' ? '#ffffff' : '#000000' }}>
                                                            {t('contact.postalCodesList.ariaInfo')}
                                                        </Typography>
                                                    </Box>
                                                </Box>

                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                                    <LocalPostOffice sx={{ color: (theme) => theme.palette.mode === 'dark' ? '#ffffff' : '#000000', fontSize: '1.5rem' }} />
                                                    <Box>
                                                        <Typography variant="body1" sx={{ fontWeight: 500, color: (theme) => theme.palette.mode === 'dark' ? '#ffffff' : '#000000' }}>
                                                            {t('contact.postalCodesList.smartDecision')}
                                                        </Typography>
                                                    </Box>
                                                </Box>

                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                                    <LocalPostOffice sx={{ color: (theme) => theme.palette.mode === 'dark' ? '#ffffff' : '#000000', fontSize: '1.5rem' }} />
                                                    <Box>
                                                        <Typography variant="body1" sx={{ fontWeight: 500, color: (theme) => theme.palette.mode === 'dark' ? '#ffffff' : '#000000' }}>
                                                            {t('contact.postalCodesList.tehran')}
                                                        </Typography>
                                                    </Box>
                                                </Box>
                                            </Box>
                                        </CardContent>
                                    </Card>
                                </Grid>
                            </Grid>
                        </Box>
                    </Grid>
                </Grid>

            </Container>
        </Box>
    );
};

export default Contact;
